import { z } from 'zod';

import type { DomainError } from './errors.js';

/**
 * Capacity contracts (M2 — PM-2020/2021/2022, ADR 0021). Capacity is stored
 * per (resource, date); zero capacity is valid and meaningful (weekend), and a
 * missing record and a zero record are behaviourally identical — the
 * missing-means-zero rule (PM-2020) is centralised in `capacityForDate`.
 * Capacity writes never touch bookings (DL-002).
 */

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected an ISO date (YYYY-MM-DD)');

/** Bulk writes cover at most ~2 years of days in one call (PM-2021). */
export const MAX_BULK_CAPACITY_ENTRIES = 800;

export const CapacityEntrySchema = z.object({
  date: isoDate,
  /** Non-negative, hundredths precision. Zero is a valid value (weekend). */
  capacityHours: z.number().min(0).multipleOf(0.01),
});
export type CapacityEntry = z.infer<typeof CapacityEntrySchema>;

// ---------------------------------------------------------------------------
// Request schemas
// ---------------------------------------------------------------------------

export const UpsertCapacityRequestSchema = z.object({
  capacityHours: z.number().min(0).multipleOf(0.01),
});
export type UpsertCapacityRequest = z.infer<typeof UpsertCapacityRequestSchema>;

/** All-or-nothing bulk write (PM-2021): every entry validates or nothing is written. */
export const BulkUpsertCapacityRequestSchema = z.object({
  entries: z.array(CapacityEntrySchema).min(1).max(MAX_BULK_CAPACITY_ENTRIES),
});
export type BulkUpsertCapacityRequest = z.infer<typeof BulkUpsertCapacityRequestSchema>;

export const CapacityListQuerySchema = z.object({
  from: isoDate.optional(),
  to: isoDate.optional(),
});
export type CapacityListQuery = z.infer<typeof CapacityListQuerySchema>;

/** Query for the period aggregation endpoint (PM-2022). */
export const PeriodCapacityRequestSchema = z.object({
  from: isoDate,
  to: isoDate,
  /** Comma-separated resource uuids. */
  resourceIds: z
    .string()
    .min(1)
    .transform((value) => value.split(','))
    .pipe(z.array(z.string().uuid()).min(1)),
});
export type PeriodCapacityRequest = z.infer<typeof PeriodCapacityRequestSchema>;

// ---------------------------------------------------------------------------
// Response schemas
// ---------------------------------------------------------------------------

export const CapacityListResponseSchema = z.object({
  entries: z.array(CapacityEntrySchema),
});
export type CapacityListResponse = z.infer<typeof CapacityListResponseSchema>;

export const PeriodCapacityResponseSchema = z.object({
  /** One row per requested resource — zero-filled when no capacity rows exist. */
  totals: z.array(
    z.object({
      resourceId: z.string().uuid(),
      capacityHours: z.number().min(0),
    }),
  ),
});
export type PeriodCapacityResponse = z.infer<typeof PeriodCapacityResponseSchema>;

// ---------------------------------------------------------------------------
// Param schemas
// ---------------------------------------------------------------------------

export const CapacityDateParamsSchema = z.object({
  id: z.string().uuid(),
  date: isoDate,
});
export type CapacityDateParams = z.infer<typeof CapacityDateParamsSchema>;

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/**
 * THE missing-means-zero helper (PM-2020). Every consumer of capacity data
 * resolves a date through this — a missing record IS zero capacity.
 */
export const capacityForDate = (map: Map<string, number>, date: string): number =>
  map.get(date) ?? 0;

/**
 * All-or-nothing bulk validation (PM-2021): collects EVERY failure across the
 * set — never fail-fast. Negative/NaN/format failures are already caught by
 * Zod; this adds the cross-entry rule (duplicate dates).
 */
export const validateCapacityEntries = (entries: CapacityEntry[]): DomainError[] => {
  const errors: DomainError[] = [];
  const seen = new Set<string>();
  for (const entry of entries) {
    if (seen.has(entry.date)) {
      errors.push({
        code: 'DUPLICATE_DATE',
        message: `Duplicate date in the entry set: ${entry.date}`,
        path: [entry.date],
      });
    }
    seen.add(entry.date);
  }
  return errors;
};
